/**
 * Stop Time Map View
 * Visar geografisk fördelning med interaktiva markörer
 */

import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { StopTimeAnalysisResult, StopTimeLocation } from '@/types/analysis';
import { XMarkIcon } from '@heroicons/react/24/outline';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix för Leaflet default icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface StopTimeMapProps {
  data: StopTimeAnalysisResult;
}

// Component för att anpassa kartan till alla markörer
const MapBounds: React.FC<{ locations: StopTimeLocation[] }> = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(
        locations.map(loc => [loc.coordinates.lat, loc.coordinates.lon] as [number, number])
      );
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [locations, map]);

  return null;
};

const StopTimeMap: React.FC<StopTimeMapProps> = ({ data }) => {
  const [filter, setFilter] = useState<'all' | 'critical' | 'high' | 'medium' | 'low'>('all');
  const [selectedLocation, setSelectedLocation] = useState<StopTimeLocation | null>(null);

  // Filtrera locations
  const filteredLocations = useMemo(() => {
    if (filter === 'all') return data.locations;
    return data.locations.filter(loc => loc.priority === filter);
  }, [data.locations, filter]);

  // Sortera för lista (efter stopptid)
  const sortedLocations = useMemo(() => {
    return [...filteredLocations].sort((a, b) => b.stopTimeDays - a.stopTimeDays);
  }, [filteredLocations]);

  // Beräkna markörstorlek baserat på stopptid
  const getMarkerSize = (stopTimeDays: number, maxDays: number) => {
    if (maxDays === 0) return 20;
    const ratio = stopTimeDays / maxDays;
    return 20 + Math.sqrt(ratio) * 40; // 20-60px
  };

  // Färg baserat på prioritet
  const getMarkerColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return '#ef4444';
      case 'high':
        return '#f97316';
      case 'medium':
        return '#eab308';
      case 'low':
        return '#22c55e';
      default:
        return '#6b7280';
    }
  };

  const maxDays = Math.max(...data.locations.map(loc => loc.stopTimeDays), 0);

  // Rank badge färg
  const getRankBadgeClass = (index: number) => {
    if (index === 0) return 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-gray-900';
    if (index === 1) return 'bg-gradient-to-br from-gray-300 to-gray-500 text-gray-900';
    if (index === 2) return 'bg-gradient-to-br from-orange-600 to-orange-800 text-white';
    return 'bg-bgSecondary text-textMuted';
  };

  return (
    <div className="space-y-4">
      {/* Detail Panel - visas när en plats är vald */}
      {selectedLocation && (
        <div className="bg-bgElevated rounded-lg p-6 border border-border animate-in slide-in-from-top-4">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-semibold text-textDefault">
              {selectedLocation.name}
            </h3>
            <button
              onClick={() => setSelectedLocation(null)}
              className="text-textSecondary hover:text-textDefault"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-4 bg-bgContainer rounded-lg">
              <div className="text-2xl font-bold text-yellow-500">
                {selectedLocation.stopTimeDays.toFixed(0)}
              </div>
              <div className="text-xs text-textSecondary mt-1">Dagar total stopptid</div>
            </div>
            <div className="text-center p-4 bg-bgContainer rounded-lg">
              <div className="text-2xl font-bold text-yellow-500">
                {selectedLocation.stopCount.toLocaleString('sv-SE')}
              </div>
              <div className="text-xs text-textSecondary mt-1">Antal stopp</div>
            </div>
            <div className="text-center p-4 bg-bgContainer rounded-lg">
              <div className="text-2xl font-bold text-yellow-500">
                {selectedLocation.vehicleCount}
              </div>
              <div className="text-xs text-textSecondary mt-1">Fordon</div>
            </div>
          </div>
          {selectedLocation.recommendation && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-green-600 dark:text-green-400">✓</span>
                <p className="text-sm text-green-800 dark:text-green-200">
                  {selectedLocation.recommendation}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-400px)]">
      {/* Map */}
      <div className="flex-1 bg-bgElevated rounded-lg border border-border overflow-hidden">
        <MapContainer
          center={[58.41, 15.62]}
          zoom={12}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MapBounds locations={filteredLocations} />
          {filteredLocations.map((location) => {
            const size = getMarkerSize(location.stopTimeDays, maxDays);
            const color = getMarkerColor(location.priority);
            
            const customIcon = L.divIcon({
              className: 'custom-marker',
              html: `
                <div style="
                  width: ${size}px;
                  height: ${size}px;
                  background: ${color};
                  border: 3px solid rgba(255,255,255,0.9);
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-family: 'JetBrains Mono', monospace;
                  font-weight: 700;
                  font-size: ${Math.max(10, size * 0.4)}px;
                  color: white;
                  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
                  box-shadow: 0 4px 15px rgba(0,0,0,0.4);
                ">
                  ${location.vehicleCount}
                </div>
              `,
              iconSize: [size, size],
              iconAnchor: [size / 2, size / 2],
            });

            return (
              <Marker
                key={location.locationId}
                position={[location.coordinates.lat, location.coordinates.lon]}
                icon={customIcon}
                eventHandlers={{
                  click: () => setSelectedLocation(location),
                }}
              >
                <Popup>
                  <div className="p-2 min-w-[200px]">
                    <div className="font-semibold text-sm mb-2">{location.name}</div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <div className="font-bold text-yellow-600">{Math.round(location.stopTimeDays)}d</div>
                        <div className="text-textSecondary">Stopptid</div>
                      </div>
                      <div>
                        <div className="font-bold text-yellow-600">{location.vehicleCount}</div>
                        <div className="text-textSecondary">Fordon</div>
                      </div>
                      <div>
                        <div className="font-bold text-yellow-600">{location.stopCount.toLocaleString('sv-SE')}</div>
                        <div className="text-textSecondary">Stopp</div>
                      </div>
                    </div>
                    {location.address && (
                      <div className="text-xs text-textTertiary mt-2">{location.address}</div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Sidebar */}
      <div className="w-full lg:w-80 bg-bgElevated rounded-lg border border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <div className="text-sm font-semibold text-textDefault mb-3">
            📍 Platser sorterade efter stopptid
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                filter === 'all'
                  ? 'bg-yellow-500 text-gray-900'
                  : 'bg-bgContainer text-textSecondary hover:bg-bgHover'
              }`}
            >
              Alla
            </button>
            <button
              onClick={() => setFilter('critical')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                filter === 'critical'
                  ? 'bg-yellow-500 text-gray-900'
                  : 'bg-bgContainer text-textSecondary hover:bg-bgHover'
              }`}
            >
              Kritisk
            </button>
            <button
              onClick={() => setFilter('high')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                filter === 'high'
                  ? 'bg-yellow-500 text-gray-900'
                  : 'bg-bgContainer text-textSecondary hover:bg-bgHover'
              }`}
            >
              Hög
            </button>
            <button
              onClick={() => setFilter('medium')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                filter === 'medium'
                  ? 'bg-yellow-500 text-gray-900'
                  : 'bg-bgContainer text-textSecondary hover:bg-bgHover'
              }`}
            >
              Medel
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {sortedLocations.map((location) => {
            const origIndex = data.locations.findIndex(l => l.locationId === location.locationId);
            return (
              <div
                key={location.locationId}
                onClick={() => {
                  setSelectedLocation(location);
                  // Scrolla till markören på kartan
                }}
                className={`p-3 mb-2 rounded-lg border cursor-pointer transition-all ${
                  selectedLocation?.locationId === location.locationId
                    ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                    : 'border-border bg-bgContainer hover:border-yellow-500 hover:translate-x-1'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 text-sm font-semibold text-textDefault line-clamp-2">
                    {location.name}
                  </div>
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold flex-shrink-0 ml-2 ${getRankBadgeClass(origIndex)}`}>
                    {origIndex + 1}
                  </span>
                </div>
                <div className="flex gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <span>⏱️</span>
                    <span className="font-mono">{Math.round(location.stopTimeDays)}d</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>🚗</span>
                    <span className="font-mono">{location.vehicleCount}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>📍</span>
                    <span className="font-mono">{location.stopCount.toLocaleString('sv-SE')}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                    location.priority === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' :
                    location.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200' :
                    location.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' :
                    'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                  }`}>
                    {location.priority === 'critical' ? 'Kritisk' :
                     location.priority === 'high' ? 'Hög' :
                     location.priority === 'medium' ? 'Medel' : 'Låg'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-4 border-t border-border bg-bgContainer">
          <div className="text-xs text-textSecondary mb-2 uppercase">Prioritet för laddinfrastruktur</div>
          <div className="text-xs text-textSecondary">
            (cirkelstorlek = stopptid, siffra = antal fordon)
          </div>
          <div className="flex justify-between mt-3 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-textSecondary">Kritisk</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-textSecondary">Hög</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-textSecondary">Medel</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-textSecondary">Låg</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default StopTimeMap;

